/*
 * Fiscal: cobros y facturas de la cuenta. Por cobrar · cobrado · vencido, y cada
 * cobro con su fecha, atraso, CFDI y monto en mono. Los montos siempre con MXN.
 */

import type { CuentaDetalle } from "../../api/tipos";
import { useAccionPago } from "../../api/hooks";
import { useAvisos } from "../../shell/Aviso";
import { useFormularios } from "../../shell/Formularios";
import { Abierto, CabeceraSeccion, fmtFecha, fmtMXN, hoyISO } from "../../ui/primitivas";
import { dias } from "../../ui/proyecto";

export function Fiscal({ c }: { c: CuentaDetalle }) {
  const { abrir } = useFormularios();
  const { avisar } = useAvisos();
  const accion = useAccionPago();
  const hoy = hoyISO();
  const pagos = [...c.pagos].sort((a, b) => a.vence.localeCompare(b.vence));
  const pend = pagos.filter((p) => p.estado === "pendiente"), cob = pagos.filter((p) => p.estado === "cobrado");
  const sum = (a: typeof pagos) => a.reduce((s, p) => s + p.monto, 0);
  const vencidos = pend.filter((p) => dias(p.vence, hoy) > 0);
  const kpi = (l: string, v: number, color: string) => (
    <div className="tarjeta" style={{ padding: "12px 16px" }}>
      <span className="tag11" style={{ color: "var(--faint)", display: "block", marginBottom: 4 }}>{l}</span>
      <span className="mono" style={{ fontSize: 22, color }}>{fmtMXN(v)}</span>
    </div>
  );
  return (
    <div style={{ maxWidth: 960 }}>
      <CabeceraSeccion titulo="Cobros y facturas" n={pagos.length} derecha={<button className="btn btn-primario" onClick={() => abrir({ tipo: "pago", cuenta: c })}>+ Nuevo cobro</button>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 12, marginBottom: 16 }}>
        {kpi("Por cobrar", sum(pend), "var(--text)")}
        {kpi("Cobrado", sum(cob), "var(--verified)")}
        {kpi("Vencido", sum(vencidos), vencidos.length ? "var(--risk)" : "var(--text)")}
      </div>
      {pagos.length === 0 ? (
        <Abierto>Sin cobros. Se crean al aceptar una propuesta o a mano con «Nuevo cobro»; todos aparecen en <strong>CxC</strong>.</Abierto>
      ) : (
        <div className="lista">
          {pagos.map((p) => {
            const d = dias(p.vence, hoy), venc = p.estado === "pendiente" && d > 0;
            return (
              <div key={p.id} style={{ display: "grid", gridTemplateColumns: "112px minmax(0,1fr) 90px 120px auto", gap: 14, alignItems: "center", padding: "11px 18px" }}>
                <span className="mono" style={{ color: venc ? "var(--risk)" : p.estado === "cobrado" ? "var(--verified)" : "var(--text)" }}>
                  {p.estado === "cobrado" ? fmtFecha(p.cobrado_en) : fmtFecha(p.vence)}
                  <span style={{ display: "block", fontSize: 11, color: venc ? "var(--risk)" : "var(--faint)" }}>{p.estado === "cobrado" ? "cobrado" : venc ? `${d} d de atraso` : d === 0 ? "vence hoy" : "vence"}</span>
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.concepto}</span>
                    {p.condicionado && <span className="abierto" style={{ flexShrink: 0, fontSize: 11, fontWeight: 500, borderRadius: 4, padding: "1px 6px" }}>condicionado</span>}
                  </span>
                  <span style={{ display: "block", fontSize: 12.5, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.proyecto}</span>
                </span>
                <span className="mono" style={{ color: p.factura ? "var(--text)" : "var(--faint)" }}>{p.factura ?? "sin CFDI"}</span>
                <span className="mono" style={{ textAlign: "right", fontSize: 13.5, color: venc ? "var(--risk)" : "var(--text)" }}>{fmtMXN(p.monto)}</span>
                <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  {p.estado === "pendiente" && !p.factura && <button className="btn btn-secundario" style={{ padding: "5px 10px", fontSize: 12.5 }} disabled={accion.isPending} onClick={async () => { const r = await accion.mutateAsync({ cuentaId: c.id, id: p.id, accion: "facturar" }); avisar(`CFDI ${r.factura} emitido para ${p.concepto}`); }}>Facturar</button>}
                  {p.estado === "pendiente" && <button className="btn btn-navy" style={{ padding: "5px 10px", fontSize: 12.5 }} disabled={accion.isPending} onClick={async () => { await accion.mutateAsync({ cuentaId: c.id, id: p.id, accion: "cobrar" }); avisar(`${fmtMXN(p.monto)} de ${c.empresa} marcado como cobrado`); }}>Cobrado</button>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
